import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../common/SEO';
import './LandingPage.css';

const calculators = [
  {
    to: '/bmi-calculator',
    emoji: '📏',
    title: 'BMI Calculator',
    desc: '5 evidence-based methods including WHO Standard, New BMI, and body surface area adjusted.',
    accent: 'var(--accent-bmi)',
    accentSoft: 'var(--accent-bmi-soft)',
    accentGhost: 'var(--accent-bmi-ghost)',
  },
  {
    to: '/calorie-intake-calculator',
    emoji: '🔥',
    title: 'Calorie Intake',
    desc: '4 formulas — Mifflin-St Jeor, Harris-Benedict, Katch-McArdle, Cunningham.',
    accent: 'var(--accent-calorie)',
    accentSoft: 'var(--accent-calorie-soft)',
    accentGhost: 'var(--accent-calorie-ghost)',
  },
  {
    to: '/ideal-weight-calculator',
    emoji: '⚖️',
    title: 'Ideal Weight',
    desc: '6 formulas — Devine, Hamwi, Robinson, Miller, Broca, and BMI benchmark.',
    accent: 'var(--accent-weight)',
    accentSoft: 'var(--accent-weight-soft)',
    accentGhost: 'var(--accent-weight-ghost)',
  },
  {
    to: '/calories-burned-calculator',
    emoji: '💪',
    title: 'Calories Burned',
    desc: '15+ activities with MET, heart rate, lean mass, and biomechanics methods.',
    accent: 'var(--accent-burned)',
    accentSoft: 'var(--accent-burned-soft)',
    accentGhost: 'var(--accent-burned-ghost)',
  },
];

const faqs = [
  { q: 'Are these calculators free?', a: 'Yes. All calculators are completely free. No signup, no paywall, no ads.' },
  { q: 'Is my data stored anywhere?', a: 'No. All calculations happen in your browser. We never collect or store your personal health data.' },
  { q: 'Which BMI formula should I use?', a: 'Standard BMI (WHO) is the global baseline. Athletes or people with extreme heights may prefer New BMI or BSA-adjusted.' },
  { q: 'Which calorie formula is most accurate?', a: 'Mifflin-St Jeor is considered the gold standard for most adults. If you know your body fat, Katch-McArdle or Cunningham are more precise.' },
  { q: 'Can I use this on my phone?', a: 'Yes. The entire app is designed mobile-first with a native app feel.' },
];

const LandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [hoveredCalc, setHoveredCalc] = useState(null);

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="landing-page">
      <SEO
        title="Kalorees — Free Health Calculators for BMI, Calories & Weight"
        description="Use our free, evidence-based health calculators to estimate BMI, daily calorie needs, ideal weight, and calories burned. No signup required. All calculations happen in your browser."
        keywords="health calculator, BMI calculator, calorie calculator, ideal weight calculator, calories burned calculator, free health tools, nutrition calculator, fitness calculator"
        canonical="/"
      />

      {/* HERO */}
      <section className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            Free Health Tools
          </div>
          <h1 className="hero-title">
            Calculate your health.<br />
            <span style={{color: 'var(--primary-dark)'}}>No signup needed.</span>
          </h1>
          <p className="hero-subtitle">
            Evidence-based calculators for BMI, calorie intake, ideal weight, and calories burned.
            All calculations happen in your browser — your data stays private.
          </p>
          <div className="hero-actions">
            <Link to="/bmi-calculator" className="hero-btn hero-btn-primary">
              Try BMI Calculator
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
            <Link to="#calculators" className="hero-btn hero-btn-secondary">
              Explore All
            </Link>
          </div>
        </div>

        {/* Floating calculator cards */}
        <div className="hero-cards">
          {calculators.map((calc, i) => (
            <Link
              key={calc.to}
              to={calc.to}
              className={`hero-card hero-card-${i} ${hoveredCalc === i ? 'hovered' : ''}`}
              style={{
                '--card-accent': calc.accent,
                '--card-soft': calc.accentSoft,
                '--card-ghost': calc.accentGhost,
              }}
              onMouseEnter={() => setHoveredCalc(i)}
              onMouseLeave={() => setHoveredCalc(null)}
            >
              <div className="hero-card-icon">{calc.emoji}</div>
              <div className="hero-card-title">{calc.title}</div>
              <div className="hero-card-arrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* MONTAGE / SHOWCASE */}
      <section className="showcase-section" id="calculators">
        <div className="container">
          <div className="showcase-header">
            <h2 className="showcase-title">Four tools. One place.</h2>
            <p className="showcase-desc">
              Each calculator uses multiple evidence-based formulas so you can compare results and find what works for your body.
            </p>
          </div>

          <div className="showcase-grid">
            {calculators.map((calc, i) => (
              <Link
                key={calc.to}
                to={calc.to}
                className="showcase-card"
                style={{
                  '--show-accent': calc.accent,
                  '--show-soft': calc.accentSoft,
                  '--show-ghost': calc.accentGhost,
                }}
              >
                <div className="showcase-card-top">
                  <div className="showcase-card-emoji">{calc.emoji}</div>
                  <div className="showcase-card-arrow">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                  </div>
                </div>
                <h3 className="showcase-card-title">{calc.title}</h3>
                <p className="showcase-card-desc">{calc.desc}</p>
                <div className="showcase-card-bar" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="how-section">
        <div className="container">
          <h2 className="how-title">How it works</h2>
          <div className="how-steps">
            {[
              { num: '01', title: 'Enter your data', desc: 'Age, height, weight, gender. All processed locally in your browser.' },
              { num: '02', title: 'Pick a formula', desc: 'Choose from multiple evidence-based calculation methods.' },
              { num: '03', title: 'Get results', desc: 'Personalized numbers with healthy ranges and actionable insights.' },
            ].map((step) => (
              <div key={step.num} className="how-step">
                <div className="how-step-num">{step.num}</div>
                <h3 className="how-step-title">{step.title}</h3>
                <p className="how-step-desc">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq-section">
        <div className="container">
          <h2 className="faq-title">Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <button
                key={i}
                className={`faq-item ${openFaq === i ? 'open' : ''}`}
                onClick={() => toggleFaq(i)}
              >
                <div className="faq-q">
                  <span>{faq.q}</span>
                  <span className="faq-icon">{openFaq === i ? '−' : '+'}</span>
                </div>
                {openFaq === i && (
                  <div className="faq-a">{faq.a}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-card">
            <h2 className="cta-title">Ready to calculate?</h2>
            <p className="cta-desc">Pick any tool and get your personalized results in seconds.</p>
            <Link to="/bmi-calculator" className="cta-btn">
              Start with BMI
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
